import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, ArrowRight, Trophy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fireConfetti } from "@/lib/confetti";
import { showXPToast } from "@/lib/xpToast";
import { ChallengeBlockList } from "@/components/creator/ChallengeBlockRenderer";

export default function CustomChallengeDayPage() {
  const { challengeId, day } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [dayData, setDayData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setAnswers({});
    setResult(null);
    try {
      const { data } = await api.get(`/custom-challenges/${challengeId}/day/${day}`);
      setDayData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load this day.");
    } finally {
      setLoading(false);
    }
  }, [challengeId, day]);

  useEffect(() => { load(); }, [load]);

  const quizBlock = dayData?.blocks?.find((b) => b.type === "quiz");
  const quiz = quizBlock?.data?.questions || [];

  const submitQuiz = async () => {
    const answerArray = quiz.map((_, i) => (answers[i] !== undefined ? answers[i] : -1));
    setSubmitting(true);
    try {
      const { data } = await api.post(`/custom-challenges/${challengeId}/day/${day}/quiz`, { answers: answerArray });
      setResult(data);
      if (data.passed) {
        showXPToast(data.xp_awarded, data.coins_awarded, `Day ${day} Complete!`);
        fireConfetti();
        refreshUser();
      } else {
        toast.error(`You scored ${data.quiz_score}% — need 75% to pass. Try again!`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="custom-challenge-day-loading">
        <Loader2 className="w-6 h-6 text-[#5C9DFF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="custom-challenge-day-error">
        <p className="text-slate-300">{error}</p>
        <Link to={`/custom-challenges/${challengeId}`} className="text-indigo-400 hover:text-indigo-300 text-sm">Back to Challenge</Link>
      </div>
    );
  }

  const allAnswered = quiz.length > 0 && quiz.every((_, i) => answers[i] !== undefined);
  const dayNum = parseInt(day, 10);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="custom-challenge-day-page">
      <button onClick={() => navigate(`/custom-challenges/${challengeId}`)} data-testid="custom-challenge-day-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Challenge
      </button>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">Day {dayData.day}</span>
        <h1 className="font-heading text-2xl font-semibold text-white mt-2" data-testid="custom-challenge-day-title">{dayData.title}</h1>
        {dayData.already_completed && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-3 py-1 mt-3">
            <CheckCircle2 className="w-3.5 h-3.5" /> Already completed — feel free to review
          </span>
        )}
      </div>

      <ChallengeBlockList blocks={dayData.blocks} challengeId={challengeId} day={parseInt(day, 10)} interactive creatorUserId={dayData.creator_user_id} />

      {quiz.length > 0 && (
      <div className="space-y-4" data-testid="custom-challenge-day-quiz">
        {quiz.map((q, qi) => (
          <div key={qi} className="glass-panel rounded-2xl p-6" data-testid={`custom-quiz-question-${qi}`}>
            <p className="text-sm font-semibold text-white mb-4">{qi + 1}. {q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => {
                const selected = answers[qi] === oi;
                const isCorrectShown = result && result.passed !== undefined;
                return (
                  <button
                    key={oi} disabled={!!result}
                    onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                    data-testid={`custom-quiz-${qi}-option-${oi}`}
                    className={`w-full text-left text-sm px-4 py-2.5 rounded-lg border transition-colors ${
                      selected ? "border-[#5C9DFF] bg-[#5C9DFF]/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25"
                    } ${isCorrectShown ? "opacity-80" : ""}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {!result ? (
          <button
            onClick={submitQuiz} disabled={!allAnswered || submitting} data-testid="custom-quiz-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-semibold px-6 py-3.5 rounded-xl transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit Quiz
          </button>
        ) : (
          <div className={`rounded-2xl p-6 text-center border ${result.passed ? "border-emerald-400/40 bg-emerald-400/5" : "border-red-400/40 bg-red-400/5"}`} data-testid="custom-quiz-result-panel">
            {result.passed ? <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" /> : <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />}
            <p className="font-heading text-lg font-semibold text-white">
              {result.passed ? (result.challenge_completed ? "Challenge Complete!" : `Day ${day} Complete!`) : "Not quite — try again"}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              Score: {result.quiz_score}% ({result.correct_count}/{result.total_questions}) {result.passed && `· +${result.xp_awarded} XP, +${result.coins_awarded} coins`}
            </p>
            {result.challenge_completed && (
              <p className="text-xs text-amber-300 mt-2 flex items-center justify-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Full 30-day challenge cleared!</p>
            )}
            <div className="flex items-center justify-center gap-3 mt-5">
              {!result.passed && (
                <button onClick={() => setResult(null)} data-testid="custom-quiz-retry-button" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
                  Try Again
                </button>
              )}
              {result.passed && !result.challenge_completed && (
                <button onClick={() => navigate(`/custom-challenges/${challengeId}/day/${dayNum + 1}`)} data-testid="custom-quiz-next-day-button" className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors">
                  Continue to Day {dayNum + 1} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
              {result.passed && result.challenge_completed && (
                <button onClick={() => navigate(`/custom-challenges/${challengeId}`)} data-testid="custom-quiz-view-challenge-button" className="flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors">
                  Back to Challenge Overview <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
